/* eslint-disable no-console */

import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import useAsync from 'react-use/lib/useAsync';
import { Entity } from '@backstage/catalog-model';

const ComponentResourcePickerFieldSchema = makeFieldSchema({
  output: z => z.string(),
  uiOptions: z =>
    z.object({
      service_name: z
        .string()
        .optional()
        .describe(
          'Entity ref of the component to list resources from, e.g. "component:default/my-app". Falls back to formData.service_name if omitted.',
        ),
      lifecycle: z
        .union([z.string(), z.array(z.string())])
        .optional()
        .describe(
          'Only include resources whose spec.lifecycle matches. Accepts a single string or an array, e.g. ["production", "ete"].',
        ),
      type: z
        .union([z.string(), z.array(z.string())])
        .optional()
        .describe(
          'Only include resources whose spec.type matches. Accepts a single string or an array, e.g. ["openshift", "database"].',
        ),
    }),
});

export const ComponentResourcePickerSchema = ComponentResourcePickerFieldSchema;

type ComponentResourcePickerProps =
  typeof ComponentResourcePickerFieldSchema.TProps;

export const ComponentResourcePicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  formContext,
  uiSchema,
  idSchema,
}: ComponentResourcePickerProps) => {
  const catalogApi = useApi(catalogApiRef);

  // Read parameters either from form context (other fields being filled) or ui:options
  const serviceName =
    formContext.formData?.service_name || uiSchema['ui:options']?.service_name;

  console.log('[ComponentResourcePicker] Extracted serviceName:', serviceName);

  const { value: resources = [] } = useAsync(async () => {
    if (!serviceName) {
      console.log(
        '[ComponentResourcePicker] Missing serviceName. Returning empty resources.',
      );
      return [];
    }

    // 1. Get component entity by service-name
    let component: Entity | undefined;
    try {
      component = await catalogApi.getEntityByRef(String(serviceName));
      console.log(
        '[ComponentResourcePicker] Fetched component:',
        component?.metadata?.name,
      );
    } catch (e) {
      console.error('[ComponentResourcePicker] Error fetching component:', e);
      return [];
    }

    if (!component) {
      console.log('[ComponentResourcePicker] Component not found.');
      return [];
    }

    // 2. Component has dependOn field, which includes all resources
    const dependOnField = component.spec?.dependsOn as string[] | undefined;
    let resourceRefs: string[] = [];

    if (Array.isArray(dependOnField)) {
      resourceRefs = dependOnField;
    } else if (component.relations) {
      // Fallback to relations if spec field isn't explicitly an array of refs
      resourceRefs = component.relations
        .filter(r => r.type === 'dependsOn')
        .map(r => r.targetRef);
    }
    console.log('[ComponentResourcePicker] Found resource refs:', resourceRefs);

    if (!resourceRefs.length) {
      console.log('[ComponentResourcePicker] No resource references found.');
      return [];
    }

    const lifecycleRaw = uiSchema['ui:options']?.lifecycle as
      string | string[] | undefined;
    let lifecycleFilter: string[] | undefined;
    if (lifecycleRaw) {
      lifecycleFilter = Array.isArray(lifecycleRaw)
        ? lifecycleRaw
        : [lifecycleRaw];
    }

    const resourceTypeRaw = uiSchema['ui:options']?.type as
      string | string[] | undefined;
    let resourceTypeFilter: string[] | undefined;
    if (resourceTypeRaw) {
      resourceTypeFilter = Array.isArray(resourceTypeRaw)
        ? resourceTypeRaw
        : [resourceTypeRaw];
    }

    // 3. Get resource entities and filter by lifecycle and resource type
    const resourceEntities: Entity[] = [];
    for (const ref of resourceRefs) {
      try {
        const resource = await catalogApi.getEntityByRef(ref);
        if (resource) {
          if (
            lifecycleFilter &&
            !lifecycleFilter.includes(resource.spec?.lifecycle as string)
          ) {
            console.log(
              `[ComponentResourcePicker] Resource ${ref} skipped (lifecycle mismatch).`,
            );
            continue;
          }
          if (
            resourceTypeFilter &&
            !resourceTypeFilter.includes(resource.spec?.type as string)
          ) {
            console.log(
              `[ComponentResourcePicker] Resource ${ref} skipped (type mismatch).`,
            );
            continue;
          }
          resourceEntities.push(resource);
          console.log(`[ComponentResourcePicker] Resource ${ref} matched!`);
        } else {
          console.log(
            `[ComponentResourcePicker] Resource ${ref} could not be resolved.`,
          );
        }
      } catch (err) {
        console.error(
          `[ComponentResourcePicker] Error fetching resource ${ref}:`,
          err,
        );
      }
    }

    console.log(
      '[ComponentResourcePicker] Final matched resource entities:',
      resourceEntities.length,
    );
    return resourceEntities;
  }, [catalogApi, serviceName, uiSchema]);

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <InputLabel htmlFor={idSchema?.$id}>
        {uiSchema['ui:title'] || 'Resource'}
      </InputLabel>
      <Select
        id={idSchema?.$id}
        value={formData || ''}
        name={idSchema?.$id}
        onChange={e => onChange(e.target.value as string)}
        displayEmpty
      >
        {resources.map(resource => {
          const stringifiedRef = `${resource.kind}:${
            resource.metadata.namespace || 'default'
          }/${resource.metadata.name}`;
          return (
            <MenuItem key={stringifiedRef} value={stringifiedRef}>
              {resource.metadata.name}
            </MenuItem>
          );
        })}
      </Select>
      <FormHelperText>
        {uiSchema['ui:description'] || 'Select a resource.'}
      </FormHelperText>
    </FormControl>
  );
};

export const ComponentResourcePickerExamples = [
  {
    description:
      'Dynamic service — reads service_name from a previous form field and lists all its dependsOn resources.',
    example: {
      'ui:field': 'ComponentResourcePicker',
      'ui:options': {},
    },
  },
  {
    description:
      'Static service with lifecycle filter — lists only ETE resources of the given component.',
    example: {
      'ui:field': 'ComponentResourcePicker',
      'ui:options': {
        service_name: 'component:default/my-app',
        lifecycle: 'ete',
      },
    },
  },
  {
    description:
      'Static service with lifecycle and type filter — lists only ETE OpenShift resources.',
    example: {
      'ui:field': 'ComponentResourcePicker',
      'ui:options': {
        service_name: 'component:default/my-app',
        lifecycle: 'ete',
        type: 'openshift',
      },
    },
  },
];
